import { isRenewablePermitDocumentType } from "encamp-shared/src/documents";
import { FeatureFlagKey } from "encamp-shared/src/featureFlags";
import { DocumentType, ExtractionStatus } from "generated-graphql/graphql";
import { useFeatureFlags } from "hooks/useFeatureFlags";
import { useGeneratePermitRenewals } from "hooks/useGeneratePermitRenewals";
import { useCallback, useEffect, useState } from "react";
import {
  canGeneratePermitRenewalForDocument,
  getPermitRenewalToggleTooltip,
} from "util/permitRenewal";

import { DOCUMENT_WITH_METADATA } from "../queries";

type UseCreatePermitRenewalOnSaveParams = {
  documentId: string | undefined;
  hasRelatedTasks: boolean;
  documentType: DocumentType | null | undefined;
  dueDate: string | Date | null | undefined;
  expirationDate: string | Date | null | undefined;
  extractionStatus: ExtractionStatus | null | undefined;
  tenantId: string | null | undefined;
  tasks?: Array<unknown> | null;
};

export function useCreatePermitRenewalOnSave({
  documentId,
  hasRelatedTasks,
  documentType,
  dueDate,
  expirationDate,
  extractionStatus,
  tenantId,
  tasks,
}: UseCreatePermitRenewalOnSaveParams) {
  const { featureFlags } = useFeatureFlags();
  const tasksModuleEnabled = !!featureFlags[FeatureFlagKey.TasksModule];
  const [createPermitRenewalTask, setCreatePermitRenewalTask] = useState(false);

  const {
    generatePermitRenewals,
    result: { loading: generatePermitRenewalsLoading },
  } = useGeneratePermitRenewals({
    refetchQueries: documentId
      ? [{ query: DOCUMENT_WITH_METADATA, variables: { id: documentId } }]
      : [],
  });

  const showToggle =
    tasksModuleEnabled && !hasRelatedTasks && isRenewablePermitDocumentType(documentType);

  const canCreate = canGeneratePermitRenewalForDocument({
    documentType,
    extractionStatus,
    tenantId,
    documentMetadata: { dueDate, expirationDate },
    tasks,
  });

  const tooltip = showToggle
    ? getPermitRenewalToggleTooltip({
        documentType,
        dueDate,
        expirationDate,
        checked: createPermitRenewalTask,
      })
    : null;

  useEffect(() => {
    setCreatePermitRenewalTask(false);
  }, [documentId]);

  useEffect(() => {
    if (!canCreate && createPermitRenewalTask) {
      setCreatePermitRenewalTask(false);
    }
  }, [canCreate, createPermitRenewalTask]);

  const maybeGenerateOnSave = useCallback(async () => {
    if (!documentId || !createPermitRenewalTask || !canCreate) {
      return;
    }
    await generatePermitRenewals([documentId]);
    setCreatePermitRenewalTask(false);
  }, [documentId, createPermitRenewalTask, canCreate, generatePermitRenewals]);

  return {
    showToggle,
    canCreate,
    tooltip,
    createPermitRenewalTask,
    setCreatePermitRenewalTask,
    maybeGenerateOnSave,
    generatePermitRenewalsLoading,
  };
}
